import { Row, Space, Table as AntdTable, TablePaginationConfig } from 'antd';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/lib/table/interface';
import { useEffect, useMemo, useState } from 'react';
import { array, useResponsive } from '@/utils';
import { VList } from 'virtuallist-antd';
import { ResetFiltersButton } from './ResetFiltersButton';
import { TableCardView } from './TableCardView';
import type { TableColumnType, TableProps } from './Table';
import {
  EMPTY_COLUMN,
  PAGINATION_DEFAULT,
  STICKY_DEFAULT,
  SYSTEM_COLUMNS,
} from './tableConstants';
import { useExcelExport } from './useExcelExport';

interface TableColumnSettings<T> {
  visibleColumns?: TableColumnType<T>[];
  columnsSelect: React.ReactNode;
  isColumnsLoading: boolean;
}

export function TableContent<T extends object>(
  props: TableProps<T> & TableColumnSettings<T>,
) {
  const {
    loading,
    columns,
    actions,
    dataSource,
    onChange,
    hideDefaultActions,
    useVirtualList,
    onDataSourceChange,
    onFiltersReset,
    rowSelection,
    hideResetFiltersAction,
    rightActions,
    responsiveCardView,
    hideExportAction,
    visibleColumns,
    columnsSelect,
    isColumnsLoading,
    sticky = STICKY_DEFAULT,
  } = props;
  const [visibleData, setVisibleData] = useState<readonly T[]>([]);

  const { isMobile, isTablet } = useResponsive();
  const virtualListComponents = useMemo(() => VList({ height: 1000 }), []);

  const exportColumnKeys = useMemo(
    () =>
      visibleColumns
        ? visibleColumns
            .map((column) => column.key!)
            .filter((key) => !SYSTEM_COLUMNS.includes(key))
        : array.empty<string>(),
    [visibleColumns],
  );

  const { exportAction } = useExcelExport(
    columns,
    visibleData,
    exportColumnKeys,
    'mb-3',
  );

  useEffect(() => {
    setVisibleData([]);
    if (onDataSourceChange) onDataSourceChange();
  }, [dataSource, onDataSourceChange]);

  const handleChange = (
    paging: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorters: SorterResult<T> | SorterResult<T>[],
    extra: TableCurrentDataSource<T>,
  ) => {
    setVisibleData(extra.currentDataSource);
    if (onChange) onChange(paging, filters, sorters, extra);
  };

  const [tableKey, setTableKey] = useState(0);

  const resetFilters = () => {
    setTableKey((currentKey) => currentKey + 1);
    if (onFiltersReset) onFiltersReset();
  };

  const isNeedToAddEmptyColumnForSelectableGrid = useMemo(
    () =>
      visibleColumns?.length === columns.length
        ? false
        : rowSelection && !visibleColumns?.find((column) => !column.width),
    [columns.length, rowSelection, visibleColumns],
  );

  const isRowSelectionAvailable = useMemo(() => {
    return !!visibleColumns && visibleColumns.length > 0;
  }, [visibleColumns]);

  if (responsiveCardView && (isMobile || isTablet) && visibleColumns) {
    return (
      <TableCardView<T>
        columns={visibleColumns}
        dataSource={dataSource}
        rowKey={(record, index) =>
          typeof props.rowKey === 'function'
            ? props.rowKey(record, index)
            : index
        }
        loading={!!loading || isColumnsLoading}
        columnsControl={!hideDefaultActions ? columnsSelect : undefined}
        rowClassName={
          typeof props.rowClassName === 'string'
            ? props.rowClassName
            : undefined
        }
      />
    );
  }

  return (
    <AntdTable<T>
      {...props}
      key={tableKey}
      title={(current) => {
        if (current.length > 0 && visibleData.length === 0) {
          setTimeout(() => setVisibleData(current), 0);
        }

        if (hideDefaultActions && !actions) return null;

        return (
          <Row justify="space-between">
            <Space>
              {!hideDefaultActions && columnsSelect}
              {!hideDefaultActions && !hideExportAction && exportAction}
              {!hideResetFiltersAction && (
                <ResetFiltersButton handleReset={resetFilters} />
              )}
              {actions}
            </Space>
            <Space>{rightActions}</Space>
          </Row>
        );
      }}
      scroll={
        props.scroll === undefined
          ? (dataSource?.length ?? 0) > 0
            ? { x: 'auto' }
            : undefined
          : props.scroll
      }
      pagination={
        props.pagination === undefined ? PAGINATION_DEFAULT : props.pagination
      }
      loading={loading || isColumnsLoading}
      rowSelection={isRowSelectionAvailable ? rowSelection : undefined}
      columns={
        isNeedToAddEmptyColumnForSelectableGrid
          ? [...visibleColumns!, EMPTY_COLUMN]
          : visibleColumns
      }
      onChange={handleChange}
      sticky={sticky}
      components={useVirtualList ? virtualListComponents : undefined}
    />
  );
}
